import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type PartnerEventDetail } from '@/api/routes/partners/partners.events.api';
import { formatEventShortDate } from '@/domain/events/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  event: PartnerEventDetail;
}

/** P12 indicators and the next dates. Confirmations are counts, never names. Zero reads "0". */
const EventDetailIndicators = ({ event }: Props) => {
  const t = useTranslate('domain.events.components.EventDetailIndicators');

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('title')}
      </Typography>
      <Box sx={sx.grid}>
        <Stat value={String(event.nextOccurrenceIntentsCount)} label={t('nextIntents')} />
        <Stat
          value={event.nextOccurrence ? formatEventShortDate(event.nextOccurrence) : t('ended')}
          label={t('nextDate')}
        />
        <Stat value={String(event.upcomingIntentsCount)} label={t('upcomingIntents')} />
      </Box>
      {event.upcomingOccurrences.length > 1 ? (
        <Stack gap={0.75}>
          <Typography variant="caption" color="text.secondary">
            {t('upcomingDates')}
          </Typography>
          <Typography variant="body2">
            {event.upcomingOccurrences.map((date) => formatEventShortDate(date)).join(' · ')}
          </Typography>
        </Stack>
      ) : null}
    </Paper>
  );
};

export default EventDetailIndicators;

const Stat = ({ value, label }: { value: string; label: string }) => (
  <Stack gap={0.25}>
    <Typography variant="h3" component="p">
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  grid: { display: 'grid', gap: 2, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
};
